import React, { useState, useEffect, useRef, useCallback, type JSX } from "react";
import { components, GroupBase, IndicatorsContainerProps, IndicatorSeparatorProps, DropdownIndicatorProps } from "react-select";
import CreatableSelect from "react-select/creatable";
import { sortOn } from "../utils/helpers";
import { IDropdownElement } from "./WeekTable";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    DialogActions,
    Button,
} from "@fluentui/react-components";
import { webLightTheme, webDarkTheme } from "@fluentui/tokens";
import { MAX_PROJECT_NAME_LENGTH } from "../utils/constants";

const PRIVATE = 0;
const PUBLIC = 1;

interface ICreatableProjectSearchInterface {
    projectList: IDropdownElement[];
    styles: any; // Updated to remove ThemePrepared dependency
    selectedProject: (project: IDropdownElement) => void;
    passSelectedProject: (project: string) => void;
    setPopUp: (value: boolean) => void;
}

interface IOptionGroup {
    label: string;
    options: IDropdownElement[];
}

export default function CreatableProjectSearch(props: ICreatableProjectSearchInterface) {
    const [options, setOptions] = useState<IOptionGroup[]>();
    const [value, setValue] = useState<IDropdownElement | null>();
    const [isPopupVisible, setIsPopupVisible] = useState<{ state: boolean; text: string | undefined }>({
        state: false,
        text: undefined,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [maxMenuHeight, setMaxMenuHeight] = useState<number>(300);
    const [menuTop, setMenuTop] = useState<number>(0);
    const [menuLeft, setMenuLeft] = useState<number>(0);
    const [menuWidth, setMenuWidth] = useState<number>(0);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const prevProjectListRef = useRef<IDropdownElement[]>(undefined); // store previous project list

    const recomputeMenuPosition = useCallback(() => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;

        const isMobileView = window.innerWidth < 768;
        setIsMobile(isMobileView);

        const viewportPadding = 8;

        if (isMobileView) {
            // On mobile, make menu full width with padding
            const top = Math.floor(rect.bottom + viewportPadding);
            const left = viewportPadding; // Small padding from screen edge
            const maxWidth = window.innerWidth - viewportPadding * 2; // Full width minus padding
            const width = Math.floor(maxWidth);
            const available = Math.floor(window.innerHeight - top - viewportPadding);
            setMenuTop(top);
            setMenuLeft(left);
            setMenuWidth(width);
            setMaxMenuHeight(Math.max(200, Math.min(available, window.innerHeight * 0.6)));
        } else {
            // Desktop behavior: keep menu fully within viewport horizontally
            const top = Math.floor(rect.bottom + 4);
            let width = Math.floor(rect.width);

            // Clamp width so that it always fits inside the viewport with padding
            const maxWidth = window.innerWidth - viewportPadding * 2;
            if (width > maxWidth) {
                width = maxWidth;
            }

            let left = Math.floor(rect.left);

            // Adjust left so the menu never overflows off-screen horizontally
            if (left + width > window.innerWidth - viewportPadding) {
                left = window.innerWidth - viewportPadding - width;
            }
            if (left < viewportPadding) {
                left = viewportPadding;
            }

            const available = Math.floor(window.innerHeight - top - viewportPadding);
            setMenuTop(top);
            setMenuLeft(left);
            setMenuWidth(width);
            setMaxMenuHeight(Math.max(160, available));
        }
    }, []);

    useEffect(() => {
        const handleResizeOrScroll = () => {
            recomputeMenuPosition();
        };
        window.addEventListener("resize", handleResizeOrScroll);
        window.addEventListener("scroll", handleResizeOrScroll, true);
        // initial
        recomputeMenuPosition();
        return () => {
            window.removeEventListener("resize", handleResizeOrScroll);
            window.removeEventListener("scroll", handleResizeOrScroll, true);
        };
    }, [recomputeMenuPosition]);

    useEffect(() => {
        prevProjectListRef.current = props.projectList;
    });

    const prevProjectList = prevProjectListRef.current; // get previous project list

    useEffect(() => {
        if (prevProjectList !== props.projectList) {
            setValue(null);

            const groups: IOptionGroup[] = [
                { label: "private", options: [] },
                { label: "public", options: [] },
            ];

            if (props.projectList) {
                props.projectList.forEach((project) => {
                    if (project.private) {
                        groups[PRIVATE].options.push(project);
                    } else {
                        groups[PUBLIC].options.push(project);
                    }
                });
            }
            sortOn(groups[PRIVATE].options, "label");
            sortOn(groups[PUBLIC].options, "label");
            setOptions(groups);
        }
    }, [props.projectList, prevProjectList]);

    const handleChange = async (newValue: IDropdownElement | null) => {
        if (newValue) {
            if (newValue.value.length > MAX_PROJECT_NAME_LENGTH) {
                console.error("Project name cannot be longer than 80 characters.");
                // Show error message popup to user
                setIsPopupVisible({
                    state: true,
                    text: `Project name cannot be longer than ${MAX_PROJECT_NAME_LENGTH} characters.`,
                });

                return;
            }

            if (newValue.value.length === 0) {
                console.error("Project name cannot be empty.");
                setIsPopupVisible({ state: true, text: "Project name cannot be empty." });
                return;
            }

            if (newValue.__isNew__ && newValue.__isNew__ === true) {
                console.info("Create new project!");
                props.setPopUp(true);
                props.passSelectedProject(newValue.value);
            } else {
                props.selectedProject(newValue);
            }
        }
    };

    const closePopup = () => {
        setIsPopupVisible({ state: false, text: undefined });
    };

    const isDarkMode = typeof document !== "undefined" && document.documentElement.getAttribute("data-color-scheme") === "dark";

    const dangerBg = isDarkMode ? webDarkTheme.colorStatusDangerBackground3 : webLightTheme.colorStatusDangerBackground3;

    const neutralBg = isDarkMode ? webDarkTheme.colorNeutralBackground1 : webLightTheme.colorNeutralBackground1;
    const neutralBgHover = isDarkMode ? webDarkTheme.colorNeutralBackground1Hover : webLightTheme.colorNeutralBackground1Hover;
    const neutralStroke = isDarkMode ? webDarkTheme.colorNeutralStroke1 : webLightTheme.colorNeutralStroke1;
    const neutralFg1 = isDarkMode ? webDarkTheme.colorNeutralForeground1 : webLightTheme.colorNeutralForeground1;
    const neutralFg2 = isDarkMode ? webDarkTheme.colorNeutralForeground2 : webLightTheme.colorNeutralForeground2;
    const createOptionColor = isDarkMode ? "#ffffff" : "#115ea3";

    const customStyles = {
        // This is the container that contains the dropdown
        container: (provided: any) => ({
            ...provided,
            // remove negative margin that caused overlap
            margin: "0.25rem 0 0 0",
        }),
        // This is the dropdown menu itself
        menu: (provided: any) => ({
            ...provided,
            position: "fixed",
            top: `${menuTop}px`,
            left: `${menuLeft}px`,
            width: `${menuWidth}px`,
            maxHeight: `${maxMenuHeight}px`,
            minWidth: isMobile ? `${menuWidth}px` : `${menuWidth}px`,
            right: isMobile ? "8px" : "auto",
            backgroundColor: neutralBg,
            color: neutralFg1,
            border: `1px solid ${neutralStroke}`,
            borderRadius: isMobile ? "8px" : "4px",
            boxShadow: isMobile
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
        }),
        // This is the portal that contains the dropdown menu
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 9999,
        }),
        // This is the list of options in the dropdown
        menuList: (provided: any) => ({
            ...provided,
            padding: isMobile ? "4px" : "2px",
            maxHeight: `${maxMenuHeight - (isMobile ? 8 : 4)}px`,
            backgroundColor: neutralBg,
        }),
        // This is the individual option in the dropdown
        option: (provided: any, { isFocused, isSelected, data }: any) => ({
            ...provided,
            color: data.__isNew__ ? createOptionColor : data.private ? "#107c10" : "#ff8c00",
            backgroundColor: isSelected ? dangerBg : isFocused ? neutralBgHover : neutralBg,
            minHeight: isMobile ? "52px" : "36px",
            padding: isMobile ? "14px 16px" : "8px 12px",
            fontSize: isMobile ? "16px" : "14px",
            fontWeight: data.__isNew__ ? 600 : 400,
            cursor: "pointer",
            margin: isMobile ? "2px 4px" : "1px 2px",
            borderRadius: isMobile ? "4px" : "2px",
            display: "flex",
            alignItems: "center",
            WebkitTapHighlightColor: "transparent",
        }),
        // This is the control that contains the input and the dropdown + button
        control: (provided: any) => ({
            ...provided,
            flexDirection: "row-reverse",
            minWidth: "100%",
            maxWidth: "100%",
            minHeight: isMobile ? "44px" : "38px",
            backgroundColor: neutralBg,
            color: neutralFg1,
            border: "none",
            fontSize: isMobile ? "16px" : "14px", // Prevents zoom on iOS
        }),
        // Value container and input container
        valueContainer: (provided: any) => ({
            ...provided,
            backgroundColor: neutralBg,
            color: neutralFg1,
        }),
        inputContainer: (provided: any) => ({
            ...provided,
            backgroundColor: neutralBg,
            color: neutralFg1,
        }),
        // Placeholder text
        placeholder: (provided: any) => ({
            ...provided,
            color: neutralFg2,
        }),
        // Single value display
        singleValue: (provided: any) => ({
            ...provided,
            color: neutralFg1,
        }),
        // Input text
        input: (provided: any) => ({
            ...provided,
            color: neutralFg1,
        }),
        // Group heading
        groupHeading: (provided: any) => ({
            ...provided,
            color: neutralFg1,
            padding: isMobile ? "10px 16px 6px" : "8px 12px 4px",
            fontSize: isMobile ? "14px" : "12px",
            fontWeight: "600",
            textTransform: "uppercase",
            marginBottom: isMobile ? "4px" : "2px",
        }),
    };

    return (
        <>
            <div ref={wrapperRef} className="w-full">
                <CreatableSelect<IDropdownElement>
                    className="react-select-container mt-1"
                    classNamePrefix="react-select"
                    styles={customStyles}
                    onChange={handleChange}
                    onInputChange={(inputValue) => inputValue}
                    options={options!}
                    getOptionLabel={(option: IDropdownElement) => `${option.label}: ${option.value}`} // create label for options (company & project name)
                    placeholder="Add project..."
                    noOptionsMessage={() => "Add new project by typing its name"}
                    value={value}
                    components={{
                        IndicatorSeparator,
                        IndicatorsContainer,
                        DropdownIndicator,
                    }}
                    formatCreateLabel={() => `Create new Project`}
                    maxMenuHeight={maxMenuHeight}
                    // Use the same DOM subtree as FluentProvider so theme tokens (CSS variables) are available
                    menuPortalTarget={document.getElementById("root") ?? document.body}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                    onMenuOpen={recomputeMenuPosition}
                    menuIsOpen={undefined} // Let react-select handle this
                    captureMenuScroll={false}
                    closeMenuOnScroll={isMobile}
                    closeMenuOnSelect={true}
                />
            </div>

            {isPopupVisible && (
                <Dialog open={isPopupVisible.state}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Error</DialogTitle>
                            <DialogContent>
                                <div>{isPopupVisible.text}</div>
                            </DialogContent>
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="primary" onClick={closePopup}>
                                        OK
                                    </Button>
                                </DialogTrigger>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            )}
        </>
    );
}

const IndicatorSeparator = ({ innerProps }: IndicatorSeparatorProps<any>) => {
    return <span className="bg-transparent" {...innerProps} />;
};

const IndicatorsContainer = (
    props: JSX.IntrinsicAttributes & IndicatorsContainerProps<IDropdownElement, false, GroupBase<IDropdownElement>>,
) => {
    return (
        <div className="bg-transparent">
            <components.IndicatorsContainer {...props} />
        </div>
    );
};

const DropdownIndicator = (
    props: JSX.IntrinsicAttributes & DropdownIndicatorProps<IDropdownElement, false, GroupBase<IDropdownElement>>,
) => {
    return (
        <components.DropdownIndicator {...props}>
            <span className="text-lg leading-none font-semibold px-1">+</span>
        </components.DropdownIndicator>
    );
};
